import { z } from "zod";

export const publicSubmissionSuccessMessage =
  "Thank you. Your submission has been received and will be reviewed by our team.";

const maxText = {
  name: 160,
  email: 255,
  phone: 40,
  title: 180,
  description: 5000,
  address: 255,
  url: 500,
  notes: 2000
};

function normalizeText(value: string) {
  return value.trim().replace(/[ \t]+/g, " ").replace(/\r\n/g, "\n");
}

const requiredText = (label: string, max = maxText.name) =>
  z
    .string({ required_error: `${label} is required.` })
    .trim()
    .min(1, `${label} is required.`)
    .max(max, `${label} must be ${max} characters or less.`)
    .transform(normalizeText);

const optionalText = (max = maxText.notes) =>
  z.preprocess(
    (value) => {
      if (typeof value !== "string") return undefined;
      const normalized = normalizeText(value);
      return normalized ? normalized : undefined;
    },
    z.string().max(max, `Must be ${max} characters or less.`).optional()
  );

const optionalUrl = z.preprocess(
  (value) => {
    if (typeof value !== "string") return undefined;
    const normalized = normalizeText(value);
    return normalized ? normalized : undefined;
  },
  z.string().url("Website/link must be a valid URL.").max(maxText.url).optional()
);

const email = z
  .string({ required_error: "Contact email is required." })
  .trim()
  .email("Enter a valid contact email.")
  .max(maxText.email)
  .transform((value) => value.toLowerCase());

const phone = z
  .string({ required_error: "Contact phone is required." })
  .trim()
  .min(1, "Contact phone is required.")
  .max(maxText.phone)
  .refine((value) => /^[0-9+()\-\s.]+$/.test(value), "Enter a valid phone number.")
  .refine((value) => value.replace(/\D/g, "").length >= 8, "Enter a valid phone number.")
  .transform(normalizeText);

const honeypot = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : ""),
  z.string().max(0, "Submission could not be accepted.")
);

const dateField = (label: string) =>
  z
    .string({ required_error: `${label} is required.` })
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, `${label} must be a valid date.`);

const timeField = z
  .string({ required_error: "Event time is required." })
  .trim()
  .regex(/^\d{2}:\d{2}$/, "Event time must be a valid time.");

export const eventSubmissionSchema = z.object({
  contactName: requiredText("Contact name"),
  contactEmail: email,
  contactPhone: phone,
  eventTitle: requiredText("Event title", maxText.title),
  eventDescription: requiredText("Event description", maxText.description).refine(
    (value) => value.length >= 20,
    "Event description must be at least 20 characters."
  ),
  eventDate: dateField("Event date"),
  eventTime: timeField,
  eventLocation: requiredText("Event location/address", maxText.address),
  websiteUrl: optionalUrl,
  organizerName: optionalText(maxText.name),
  extraNotes: optionalText(maxText.notes),
  company: honeypot
});

export const dealSubmissionSchema = z.object({
  contactName: requiredText("Contact name"),
  contactEmail: email,
  contactPhone: phone,
  businessName: requiredText("Business name", maxText.title),
  dealTitle: requiredText("Deal title", maxText.title),
  dealDescription: requiredText("Deal description", maxText.description).refine(
    (value) => value.length >= 20,
    "Deal description must be at least 20 characters."
  ),
  dealExpiryDate: dateField("Deal expiry date"),
  websiteUrl: optionalUrl,
  businessAddress: optionalText(maxText.address),
  termsAndConditions: optionalText(maxText.notes),
  extraNotes: optionalText(maxText.notes),
  company: honeypot
});

export type EventSubmission = z.infer<typeof eventSubmissionSchema>;
export type DealSubmission = z.infer<typeof dealSubmissionSchema>;

export function validationDetails(error: z.ZodError) {
  return error.flatten().fieldErrors;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatOptional(value?: string) {
  return value && value.trim() ? value : "Not provided";
}

function textLine(label: string, value?: string) {
  return `${label}: ${formatOptional(value)}`;
}

function htmlRow(label: string, value?: string) {
  return `<tr><th align="left" style="padding:6px 12px 6px 0;vertical-align:top;">${escapeHtml(label)}</th><td style="padding:6px 0;white-space:pre-wrap;">${escapeHtml(formatOptional(value))}</td></tr>`;
}

function submittedAt() {
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: "Australia/Sydney"
  }).format(new Date());
}

export function buildEventSubmissionEmail(data: EventSubmission) {
  const submitted = submittedAt();
  const rows: Array<[string, string | undefined]> = [
    ["Submission type", "Event"],
    ["Contact name", data.contactName],
    ["Contact email", data.contactEmail],
    ["Contact phone", data.contactPhone],
    ["Event title", data.eventTitle],
    ["Event description", data.eventDescription],
    ["Event date", data.eventDate],
    ["Event time", data.eventTime],
    ["Event location/address", data.eventLocation],
    ["Website/link", data.websiteUrl],
    ["Organizer name", data.organizerName],
    ["Extra notes", data.extraNotes],
    ["Date/time submitted", submitted],
    ["Review note", "Submitted from the public website and needs manual review before publishing."]
  ];

  return {
    subject: `New Event Submission: ${data.eventTitle}`,
    html: buildHtmlEmail("New public event submission", rows),
    text: buildTextEmail(rows)
  };
}

export function buildDealSubmissionEmail(data: DealSubmission) {
  const submitted = submittedAt();
  const rows: Array<[string, string | undefined]> = [
    ["Submission type", "Deal"],
    ["Contact name", data.contactName],
    ["Contact email", data.contactEmail],
    ["Contact phone", data.contactPhone],
    ["Business name", data.businessName],
    ["Deal title", data.dealTitle],
    ["Deal description", data.dealDescription],
    ["Deal expiry date", data.dealExpiryDate],
    ["Website/link", data.websiteUrl],
    ["Business address", data.businessAddress],
    ["Terms and conditions", data.termsAndConditions],
    ["Extra notes", data.extraNotes],
    ["Date/time submitted", submitted],
    ["Review note", "Submitted from the public website and needs manual review before publishing."]
  ];

  return {
    subject: `New Deal Submission: ${data.dealTitle}`,
    html: buildHtmlEmail("New public deal submission", rows),
    text: buildTextEmail(rows)
  };
}

function buildHtmlEmail(title: string, rows: Array<[string, string | undefined]>) {
  return `
    <div>
      <h1>${escapeHtml(title)}</h1>
      <table cellpadding="0" cellspacing="0" role="presentation">
        <tbody>
          ${rows.map(([label, value]) => htmlRow(label, value)).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function buildTextEmail(rows: Array<[string, string | undefined]>) {
  return rows.map(([label, value]) => textLine(label, value)).join("\n");
}
